import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AppInformationService, AuthService, GenesisUnitOfWork, ReportService } from 'app/core/core.module';
import { Delivery } from 'app/core/model/entity-model';
import { ActivatedRoute, Router } from '@angular/router';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { BsModalService } from 'ngx-bootstrap/modal';
import { alert, confirm } from 'devextreme/ui/dialog';

@Component({
    selector: 'app-delivery-overview',
    templateUrl: './delivery-overview.component.html',
    styleUrls: ['./delivery-overview.component.scss'],
    standalone: false
})
export class DeliveryOverviewComponent implements OnInit, OnDestroy {
    deliveries: Delivery[];
    showCompleted: boolean = false;
    showStackFlyout = false;
    openedDelivery: Delivery;
    isFlyoutReadOnly: boolean;
    sortMode: boolean = false;
    mode: 'stack' | 'delivery';

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;
    onOptionChangedShared = SharedModule.onOptionChanged;

    constructor(
        protected uow: GenesisUnitOfWork,
        public auth: AuthService,
        private appInfo: AppInformationService,
        private reportService: ReportService,
        private route: ActivatedRoute,
        private modalService: BsModalService
    ) {}

    async ngOnInit() {
        const drivers = this.uow.deliveryDrivers.all();
        const trucks = this.uow.deliveryTrucks.all();
        const trailers = this.uow.deliveryTrailers.all();
        this.mode = this.route.snapshot.data['mode'];
        const transportPlates = this.uow.deliveryTransportPlates.all();
        await this.getData(false);
    }

    ngOnDestroy() {
        this.uow.rollback();
    }

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }

    hasChanges() {
        return this.uow.hasChanges();
    }

    async getData(showCompleted?: boolean) {
        const deliveries = await this.uow.getDeliveries('deliveryStacks.orderLineDetail.orderLine.order.dealer', showCompleted, this.mode);
        this.deliveries = deliveries;
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                template: 'toolbarText',
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'revert',
                    elementAttr: { title: 'Reset Filters' },
                    onClick: () => {
                        e.component.clearFilter();
                    },
                },
                location: 'after',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            }
        );
    }

    async openStack(delivery: Delivery) {
        if (!delivery) return;
        this.openedDelivery = delivery;
        this.isFlyoutReadOnly = !!delivery.completed;
        this.showStackFlyout = true;
    }

    closeStack() {
        this.showStackFlyout = false;
        this.openedDelivery = null;
    }

    async rowDoubleClick(e) {
        if (this.uow.hasChanges() || !e.data) return;
        await this.openStack(e.data);
    }
    async print() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const selectedRow = selectedRows[0];
        this.reportService.printDeliveryReport(selectedRow.id);
    }

    async complete() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        const result = await confirm(`Are you ready to mark as complete?`, `${this.appInfo.settings.company}`);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        selectedRows.forEach((row) => {
            if (this.mode === 'delivery') {
                if (!row.completed) {
                    row.completed = new Date();
                    row.completedBy = this.auth.currentUser.userName;
                }
            }
            if (this.mode === 'stack') {
                if (!row.stackCompleted) {
                    row.stackCompleted = new Date();
                    row.stackCompletedBy = this.auth.currentUser.userName;
                }
            }
        });
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }
    async delete(delivery: Delivery) {
        if (!delivery || delivery.completed) return;

        const success = await confirm('Are you sure you want to delete this delivery?', this.appInfo.settings.company);
        if (!success) return;

        const date = new Date();
        delivery.deleted = date;
        delivery.deletedBy = this.auth.currentUser.userName;
        delivery.deliveryStacks.forEach((stack) => {
            stack.deleted = date;
            stack.deletedBy = this.auth.currentUser.userName;
        });
        await this.uow.commit([delivery, ...delivery.deliveryStacks]);
        await this.getData();

        if (this.openedDelivery && delivery.id === this.openedDelivery.id) this.closeStack();
    }
}
